import { useQuery } from '@tanstack/react-query'

import type { AppConfig } from '@/api/config/configService'
import { CURRENT_APP_VERSION, fetchLiveAppVersion } from '@/lib/appVersion'
import { isVersionLessThan, reloadNativeLiveUpdate, syncNativeLiveUpdate } from '@/lib/liveUpdate'
import { getNativePlatform, isNativePlatform } from '@/lib/native'
import { QUERY_KEYS } from '@/lib/queryKeys'

const POLL_INTERVAL_MS = 5 * 60 * 1000
const isVersionCheckEnabled = !import.meta.env.DEV && !!CURRENT_APP_VERSION

export function useAppVersionCheck(appConfig?: AppConfig) {
  const nativeLiveUpdateEnabled = isNativePlatform() && !import.meta.env.DEV

  const { data: liveAppVersion } = useQuery({
    queryKey: [QUERY_KEYS.APP_VERSION],
    queryFn: fetchLiveAppVersion,
    enabled: isVersionCheckEnabled && !isNativePlatform(),
    staleTime: 0,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: false,
  })

  const { data: nativeUpdateReady } = useQuery({
    queryKey: [QUERY_KEYS.NATIVE_LIVE_UPDATE],
    queryFn: syncNativeLiveUpdate,
    enabled: nativeLiveUpdateEnabled,
    staleTime: 0,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: false,
  })

  const webUpdateAvailable = !!liveAppVersion?.version && liveAppVersion.version !== CURRENT_APP_VERSION
  const updateAvailable = nativeLiveUpdateEnabled ? !!nativeUpdateReady : webUpdateAvailable
  const forceUpdate =
    isNativePlatform() &&
    !!appConfig?.minNativeAppVersion &&
    isVersionLessThan(CURRENT_APP_VERSION || '0.0.0', appConfig.minNativeAppVersion)

  const platform = getNativePlatform()
  const storeUrl = platform === 'ios' ? appConfig?.iosStoreUrl : platform === 'android' ? appConfig?.androidStoreUrl : undefined

  const refresh = () => {
    if (nativeLiveUpdateEnabled) {
      void reloadNativeLiveUpdate()
      return
    }
    window.location.reload()
  }

  return { updateAvailable: updateAvailable && !forceUpdate, refresh, forceUpdate, storeUrl }
}
